import type { ContentPropertiesQueryContentNodeType } from '@confluence/content-body/entry-points/ContentPropertiesQuery.ts';
import type { ContentUnifiedQueryContentNodeType } from '@confluence/content-unified-query';
import { fg } from '@confluence/feature-gating';

type propType =
	| Partial<ContentUnifiedQueryContentNodeType | ContentPropertiesQueryContentNodeType>
	| null
	| undefined;

// editor property is expected to be:
//   1. ""    -> auto conversion will happen
//   2. "v2"  -> explicitly using fabric editor
//   3. "v1"  -> explicitly using tinyMCE editor
// when parsing editor property, if neither of the above data is received,
// then it's an unexpected case and we set it as "unset" for analytics.
function parseEditorProperty(value: string | null | undefined) {
	if (!value) {
		return '';
	}
	if (value?.includes('v2')) {
		return 'v2';
	}
	if (value?.includes('v1')) {
		return 'v1';
	}
	return 'unset';
}

export function getAutoConvertionInfo(content: propType): {
	queryDataId: string | null | undefined;
	editorProperty: string;
	fabricEditorSupported: boolean | null | undefined;
	isAutoConverted: boolean | undefined;
} {
	const contentProperties = content?.properties?.nodes;
	const editorInfo = contentProperties?.filter((p: any) => p?.key === 'editor');
	const editorPropertieValueRaw = editorInfo?.[0]?.value;
	const editorProperty = parseEditorProperty(editorPropertieValueRaw);
	if (
		content &&
		'metadata' in content &&
		content?.metadata?.frontend &&
		'fabricEditorSupported' in content.metadata.frontend
	) {
		return {
			queryDataId: content?.id,
			editorProperty,
			fabricEditorSupported: content?.metadata?.frontend?.fabricEditorSupported,
			isAutoConverted: fg('confluence_editor_auto-convert_changes')
				? // if a page is not eligible we never auto-convert it
					!!content?.metadata?.frontend?.fabricEditorSupported &&
					// For pages migrated from DC, with editor property not set, it's easy,
					// they'll always be auto-converted. We explicitly set the editor property
					// to v1 when reverting back to TinyMCE.
					(editorProperty === '' ||
						// For pages created in cloud, with editor property set to v1, we only want to show
						// the auto-conversion settings when it is initially converted to fabric editor.
						// This means the editor property is still set to TinyMCE (v1) but the page is Fabric Editor.
						(editorProperty === 'v1' &&
							content?.body?.dynamic?.representation === 'atlas_doc_format'))
				: undefined,
		};
	}
	return {
		queryDataId: content?.id,
		editorProperty,
		fabricEditorSupported: null,
		isAutoConverted: fg('confluence_editor_auto-convert_changes') ? false : undefined,
	};
}
